import { AfterViewChecked, ChangeDetectorRef, Component, Input, OnChanges, OnInit } from "@angular/core";
import { NavigationService } from "src/services/navigation/navigation.service";
import { HttpService } from "src/services/http/http.service";
import { environment } from "@environments/environment";
import { MessageService } from "src/services/message/message.service";
import { OrderDeletionRequest } from "../../../../shared/local-api.types";
import { LoadingController } from "@ionic/angular";
import { Order } from "src/services/order/order.interface";

import { tryParseDate } from "src/services/utils/timestamp.utils";
import { User, userToString } from "src/services/user/user.interface";
import { SubscriptionApiService } from "src/services/subscription/subscription-api.service";
import { AbstractControl, FormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { orderFormFields } from "./order.form";
import { Subscription } from "src/services/subscription/subscription";
import { BaseFormObjectComponent } from "../form-field/base-form-object.component";
import { PendingOrder } from "src/services/pending-order/pending-order.interface";
import { Item } from "src/services/item/item.interface";
import { ItemApiService } from "src/services/item/item-api.service";
import { getNextStep } from "src/services/order-status/order-step.utils";
import { OrderHistoryService } from "src/services/history/order-history.service";
import { AuthService } from "src/services/auth/auth.service";
import { orderFulfillmentTimeValidator } from "../../../../services/utils/form.validators";

@Component({
  selector: "rc-full-order-card",
  templateUrl: "./full-order-card.component.html",
  styleUrls: ["./full-order-card.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: FullOrderCardComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: FullOrderCardComponent,
    },
  ],
  standalone: false,
})
export class FullOrderCardComponent
  extends BaseFormObjectComponent<Order | PendingOrder>
  implements OnInit, OnChanges, AfterViewChecked
{
  @Input() public mapZoom: number = 19;
  @Input() public disablePinchZoom: boolean = false;
  @Input() public showAccommodations = false;
  @Input() public showAddToCalender = true;
  @Input() public showDateDetails = false;
  @Input() public showDeleteButton = false;
  @Input() public showMapByDefault = true;
  @Input() public showOrderMetadata = true;
  @Input() public showOrderType = true;
  @Input() public showUserSelection = true;
  @Input() public user: User | undefined;
  protected currentUserIsAdminOrDriver: boolean = false;
  protected form = this.formBuilder.group({
    [orderFormFields.address]: null,
    [orderFormFields.fulfillmentDate]: ["", [(control: AbstractControl) => Validators.required(control)]],
    [orderFormFields.fulfillmentTime]: ["", [(control: AbstractControl) => Validators.required(control)]],
    [orderFormFields.fulfillmentTimeOther]: [
      "",
      [
        (control: AbstractControl) =>
          orderFulfillmentTimeValidator(control, this.form?.get(orderFormFields.fulfillmentTime).value),
      ],
    ],
    [orderFormFields.preferences]: null,
    [orderFormFields.serviceType]: ["", [(control: AbstractControl) => Validators.required(control)]],
    [orderFormFields.status]: ["", [(control: AbstractControl) => Validators.required(control)]],
    [orderFormFields.step]: null,
    [orderFormFields.userId]: ["", [(control: AbstractControl) => Validators.required(control)]],
  });
  protected items: Item[];
  protected orderForNonEditableProperties: Order | PendingOrder;
  protected readonly orderFormFields = orderFormFields;
  protected showMap: boolean;
  protected subscriptions: Subscription[];
  protected readonly tryParseDate = tryParseDate;
  protected readonly userToString = userToString;
  private lastFetchedSubscriptionUserId: string | undefined;

  constructor(
    public readonly navigationService: NavigationService,
    public readonly orderHistoryService: OrderHistoryService,
    private readonly authService: AuthService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly httpService: HttpService,
    private readonly itemService: ItemApiService,
    private readonly loadingController: LoadingController,
    private readonly messageService: MessageService,
    private readonly subscriptionService: SubscriptionApiService,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    if (this.readOnly) {
      this.form.disable();
    }
    this.currentUserIsAdminOrDriver = await this.authService.isActiveUserAdminOrDriver();
    await this.fillData();
    super.ngOnInit();

    if (this.orderForNonEditableProperties) {
      this.form.patchValue(
        Object.create({
          ...this.orderForNonEditableProperties,
        }),
      );
    }
    this.form.valueChanges.subscribe(async (formValue) => {
      if (!!formValue.userId && formValue.userId !== this.orderForNonEditableProperties?.userId) {
        await this.fetchSubscription(formValue.userId);
      }
    });
  }

  public async ngOnChanges(): Promise<void> {
    const nextStep = getNextStep(this.orderForNonEditableProperties);
    this.form.patchValue({ [orderFormFields.step]: nextStep });
    await this.fillData();
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }

  public logAddedToCalendar(): void {
    const orderId = this.orderForNonEditableProperties?.id;
    if (orderId) {
      this.orderHistoryService.logAddedToCalendar(orderId);
    }
  }

  public async requestDelete(): Promise<void> {
    const orderId = this.orderForNonEditableProperties?.id;
    if (!orderId) {
      return;
    }

    const loading = await this.loadingController.create();
    await loading.present();

    try {
      await this.httpService.sendPostRequest<OrderDeletionRequest, void>(environment.endpoints.orderDeletionRequest, {
        orderId,
      });
      await this.messageService.presentAlert(undefined, undefined, "Your request has been submitted.");
    } catch {
      await this.messageService.presentAlert(undefined, undefined, "An error occurred while processing your request.");
    } finally {
      await loading.dismiss();
    }
  }

  public async setDisabledState(disabled: boolean): Promise<void> {
    super.setDisabledState(disabled);
    await this.fillData();
  }

  public hideMap(): void {
    this.showMap = false;
  }

  public unhideMap(): void {
    this.showMap = true;
  }

  public validate(_control: AbstractControl): object | null {
    if (this.form.valid) {
      return null;
    }

    return this.getAllFormErrors();
  }

  public toDisplayValue(_input: Order): string {
    return "";
  }

  private async fillData(): Promise<void> {
    this.showMap = this.showMapByDefault;
    this.orderForNonEditableProperties = this.initialValue;

    const userId = this.orderForNonEditableProperties?.userId;
    if (this.showAddToCalender && userId) {
      this.items = await this.itemService.getForOrder({ items: this.orderForNonEditableProperties.items, userId });
    }

    if (!this.user) {
      return;
    }

    await this.fetchSubscription(this.user.id);
  }

  private async fetchSubscription(userId: string): Promise<void> {
    if (this.lastFetchedSubscriptionUserId === userId) {
      return;
    }
    this.lastFetchedSubscriptionUserId = userId;
    this.subscriptions = await this.subscriptionService.getByUserId(userId);
  }
}
